import React, { useState, useEffect, useCallback } from 'react';
import { teamApi, TeamMember } from '../../api/teamApi';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { stringToColor, getInitials } from '../../utils/helpers';
import './Team.css';

export function Team() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await teamApi.getMyTeam();
      setTeamMembers(response.team);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const filteredMembers = teamMembers.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.employee_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalHoursThisWeek = teamMembers.reduce((sum, m) => sum + m.hours_this_week, 0);
  const totalPending = teamMembers.reduce((sum, m) => sum + m.pending_timesheets, 0);

  return (
    <div className="team-page">
      <header className="team-header">
        <div className="team-title">
          <h1>My Team</h1>
          <p>View and manage your direct reports</p>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <div className="team-stats">
        <Card className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#3b82f615', color: '#3b82f6' }}>
            <Icons.Users />
          </div>
          <div className="stat-info">
            <span className="stat-label">Team Size</span>
            <span className="stat-value">{teamMembers.length}</span>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#10b98115', color: '#10b981' }}>
            <Icons.Clock />
          </div>
          <div className="stat-info">
            <span className="stat-label">Hours This Week</span>
            <span className="stat-value">{totalHoursThisWeek.toFixed(1)}</span>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
            <Icons.FileText />
          </div>
          <div className="stat-info">
            <span className="stat-label">Pending Approvals</span>
            <span className="stat-value">{totalPending}</span>
          </div>
        </Card>
      </div>

      <Card className="team-list-card">
        <div className="team-toolbar">
          <div className="search-box">
            <Icons.Search />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="refresh-btn" onClick={fetchTeam}>
            <Icons.Refresh />
            Refresh
          </button>
        </div>

        {filteredMembers.length === 0 ? (
          <div className="empty-state">
            <Icons.Users />
            <h3>No Team Members</h3>
            <p>
              {teamMembers.length === 0
                ? "You don't have any direct reports assigned to you."
                : "No team members match your search."}
            </p>
          </div>
        ) : (
          <div className="team-table-container">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Role</th>
                  <th>Hours This Week</th>
                  <th>Pending</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div className="employee-cell">
                        <div
                          className="avatar"
                          style={{ backgroundColor: stringToColor(member.email) }}
                        >
                          {getInitials(member.name.split(' ')[0], member.name.split(' ')[1] || '')}
                        </div>
                        <div className="employee-info">
                          <span className="employee-name">{member.name}</span>
                          <span className="employee-email">{member.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>{member.department || '-'}</td>
                    <td>
                      <div className="roles">
                        {member.roles.map((role) => (
                          <span key={role} className={`role-badge role-${role}`}>
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="hours-cell">
                        <span className="hours-value">{member.hours_this_week.toFixed(1)}h</span>
                        <div className="hours-bar">
                          <div
                            className="hours-fill"
                            style={{ width: `${Math.min(100, (member.hours_this_week / 40) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      {member.pending_timesheets > 0 ? (
                        <span className="pending-badge">{member.pending_timesheets}</span>
                      ) : (
                        <span className="no-pending">-</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${member.hours_this_week >= 40 ? 'complete' : 'in-progress'}`}>
                        {member.hours_this_week >= 40 ? 'Complete' : 'In Progress'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default Team;
